import { DiscreteEvent, dispatchCustomEvent, KPI } from '@utils/customEvents'
import type { Enrollment } from '@clear-denver/tpc-openapi-typescript'
import { session } from './session'
import { formatReason, formatMeta } from './utils'
import {
  AuthPipeMeta,
  AuthViewEvent,
  ResultMeta,
  InitiateResult,
  VerifyResult,
  SignInResult,
  SubmitCitizenshipResult,
  SubmitIdentityResult,
  SearchEligibilityResult,
  AcceptCoppaResult,
  RenewalEligibilityResult,
  PREFIX,
} from './types'

const LOOP_THRESHOLD = 3

const BASE_META_KEYS: (keyof AuthPipeMeta)[] = [
  'xCLEARCorrelationInfo',
  'authFlowSessionId',
  'flow',
  'route',
  'experimentEnabled',
  'experimentVariant',
  'experimentUID',
]

export function createAuthPipe(meta: AuthPipeMeta) {
  session.meta = meta
  const baseMeta = {
    ...meta,
    kpi: KPI.USER_INTERACTION,
  }

  function updateBaseMeta(props: ResultMeta) {
    for (const key of BASE_META_KEYS) {
      if (key in props && props[key] != null) {
        ;(baseMeta as Record<string, unknown>)[key] = props[key]
      }
    }
  }

  function info(opts: {
    message: string
    event: DiscreteEvent
    meta?: ResultMeta
    sync?: boolean
  }) {
    if (opts.meta) updateBaseMeta(opts.meta)
    const payload = {
      payload: { message: PREFIX + opts.message },
      metadata: { ...baseMeta, ...opts.meta, event: opts.event },
    }
    if (session.debug) console.info(`[AUTH:${opts.event}]`, payload)
    const dispatch = () =>
      dispatchCustomEvent('INFO' as Enrollment.LogEntryLevelEnum.INFO, payload)

    /** make resilient to conditional nav on site */
    opts.sync ? dispatch() : queueMicrotask(dispatch)
  }

  function fail(opts: {
    type: string
    reason: string
    event?: DiscreteEvent
    meta?: ResultMeta
    sync?: boolean
  }) {
    if (opts.meta) updateBaseMeta(opts.meta)
    session.trackEvent(`fail:${opts.type}`)
    const payload = {
      payload: { message: PREFIX + opts.reason },
      metadata: {
        ...baseMeta,
        ...opts.meta,
        type: opts.type,
        reason: opts.reason,
        event: opts.event ?? DiscreteEvent.CLIENT_SIDE_ERROR,
      },
    }
    if (session.debug) console.error(`[AUTH:${opts.type}]`, payload)
    const dispatch = () => {
      dispatchCustomEvent(
        'ERROR' as Enrollment.LogEntryLevelEnum.ERROR,
        payload
      )
    }

    /** make resilient to conditional nav on site */
    opts.sync ? dispatch() : queueMicrotask(dispatch)
  }

  return {
    view: ({
      event,
      startSession = false,
      landed = false,
      ...meta
    }: {
      event: AuthViewEvent
      startSession?: boolean
      landed?: boolean
    } & ResultMeta) => {
      if (startSession) {
        session.resetSession()
        baseMeta.authFlowSessionId = session.authFlowSessionId
      }
      if (landed) {
        session.resetRedirects()
      }
      if (Object.keys(meta).length) updateBaseMeta(meta)
      session.meta = { ...baseMeta }
      session.trackEvent(`view:${event}${landed ? ':landed' : ''}`)
      info({
        message: 'Page viewed',
        event,
        meta: { landed: `${landed}`, ...meta },
      })
      if (landed) {
        info({
          message: 'User settled on the page',
          event: DiscreteEvent.AUTH_FLOW_LANDED,
          meta: { page: event, ...meta },
        })
      }
    },

    redirect: ({
      from,
      reason,
      ...meta
    }: { from: string; reason: string } & ResultMeta) => {
      const count = session.trackRedirect(from)
      info({
        message: 'Redirect to login',
        event: DiscreteEvent.AUTH_REDIRECT_TO_LOGIN,
        meta: { from, reason, ...meta },
      })
      if (count >= LOOP_THRESHOLD) {
        fail({
          type: 'LOOP',
          reason: `Redirect loop detected (${count})`,
          meta: {
            count: String(count),
            history: session.redirectHistory.join(' → '),
            ...meta,
          },
        })
      }
    },

    initiate: (result: InitiateResult) => {
      session.trackEvent(`initiate:${result.status}`)
      switch (result.status) {
        case 'begin': {
          const { status: _, ...meta } = result
          info({
            message: 'Initiate auth begin',
            event: DiscreteEvent.INITIATE_AUTH_BEGIN,
            meta,
          })
          break
        }
        case 'success': {
          const { status: _, hasOtpSessionId, ...meta } = result
          if (hasOtpSessionId) {
            info({
              message: 'Initiate auth successful',
              event: DiscreteEvent.INITIATE_AUTH_SUCCESS,
              meta,
            })
          } else {
            fail({
              type: 'INITIATE',
              reason: 'API returned 200 without otpSessionId',
              meta,
            })
          }
          break
        }
        case 'failure': {
          const { status: _, error, ...meta } = result
          fail({
            type: 'INITIATE',
            event: DiscreteEvent.INITIATE_AUTH_FAILURE,
            reason: formatReason(error),
            meta: { ...formatMeta(error), ...meta },
          })
          break
        }
        default: {
          throw new Error(
            JSON.stringify(
              {
                type: 'INITIATE',
                reason: `Status not supported: ${
                  (result as Record<string, unknown>).status
                }`,
              },
              null,
              2
            )
          )
        }
      }
    },

    verify: (result: VerifyResult) => {
      session.trackEvent(`verify:${result.status}`)
      switch (result.status) {
        case 'begin': {
          const { status: _, ...meta } = result
          info({
            message: 'OTP verification begin',
            event: DiscreteEvent.OTP_VERIFICATION_BEGIN,
            meta,
          })
          break
        }
        case 'success': {
          const { status: _, hasDesiredPath, ...meta } = result
          info({
            message: 'OTP verification successful',
            event: DiscreteEvent.OTP_VERIFICATION_SUCCESS,
            meta: { hasDesiredPath: String(hasDesiredPath), ...meta },
          })
          if (!hasDesiredPath) {
            fail({
              type: 'DEAD_END',
              reason: 'No desired path after verification',
              meta,
            })
          }
          break
        }
        case 'failure': {
          const { status: _, error, attempts, ...meta } = result
          fail({
            type: 'VERIFY',
            event: DiscreteEvent.OTP_VERIFICATION_FAILURE,
            reason: formatReason(error),
            meta: {
              attempts: String(attempts),
              ...formatMeta(error),
              ...meta,
            },
          })
          break
        }
        default: {
          throw new Error(
            JSON.stringify(
              {
                type: 'VERIFY',
                reason: `Status not supported: ${
                  (result as Record<string, unknown>).status
                }`,
              },
              null,
              2
            )
          )
        }
      }
    },

    signIn: (result: SignInResult) => {
      session.trackEvent(`signIn:${result.status}`)
      switch (result.status) {
        case 'begin': {
          const { status: _, page, ...meta } = result
          info({
            message: 'Biographics submit begin',
            event: DiscreteEvent.AUTH_SUBMIT_BIOGRAPHICS_BEGIN,
            meta: { page, ...meta },
          })
          break
        }
        case 'success': {
          const { status: _, ...meta } = result
          info({
            message: 'Biographics submit successful',
            event: DiscreteEvent.AUTH_SUBMIT_BIOGRAPHICS_SUCCESS,
            meta,
          })
          break
        }
        case 'failure': {
          const { status: _, error, ...meta } = result
          fail({
            type: 'SIGNIN',
            event: DiscreteEvent.AUTH_SUBMIT_BIOGRAPHICS_FAILURE,
            reason: formatReason(error),
            meta: { ...formatMeta(error), ...meta },
          })
          break
        }
        default: {
          throw new Error(
            JSON.stringify(
              {
                type: 'SIGNIN',
                reason: `Status not supported: ${
                  (result as Record<string, unknown>).status
                }`,
              },
              null,
              2
            )
          )
        }
      }
    },

    submitCitizenship: (result: SubmitCitizenshipResult) => {
      session.trackEvent(`submitCitizenship:${result.status}`)
      switch (result.status) {
        case 'begin': {
          const { status: _, ...meta } = result
          info({
            message: 'Submit pre biographic citizenship begin',
            event: DiscreteEvent.SUBMIT_PREBIOGRAPHIC_CITIZENSHIP_BEGIN,
            meta,
          })
          break
        }
        case 'success': {
          const { status: _, existenceStatus, expirationDate, ...meta } = result
          info({
            message: 'Submit pre biographic citizenship successful',
            event: DiscreteEvent.SUBMIT_PREBIOGRAPHIC_CITIZENSHIP_SUCCESSFUL,
            meta: {
              status: existenceStatus,
              ...(expirationDate
                ? { expirationDate: expirationDate.toISOString() }
                : {}),
              ...meta,
            },
          })
          break
        }
        case 'failure': {
          const { status: _, error, ...meta } = result
          fail({
            type: 'CITIZENSHIP',
            event: DiscreteEvent.SUBMIT_PREBIOGRAPHIC_CITIZENSHIP_FAILURE,
            reason: formatReason(error),
            meta: { ...formatMeta(error), ...meta },
          })
          break
        }
        default: {
          throw new Error(
            JSON.stringify(
              {
                type: 'CITIZENSHIP',
                reason: `Status not supported: ${
                  (result as Record<string, unknown>).status
                }`,
              },
              null,
              2
            )
          )
        }
      }
    },

    submitIdentity: (result: SubmitIdentityResult) => {
      session.trackEvent(`submitIdentity:${result.status}`)
      switch (result.status) {
        case 'begin': {
          const { status: _, ...meta } = result
          info({
            message: 'Identity submit has begun',
            event: DiscreteEvent.IDENTITY_SUBMIT_START,
            meta,
          })
          break
        }
        case 'success': {
          const { status: _, ...meta } = result
          info({
            message: 'Identity submit successful',
            event: DiscreteEvent.IDENTITY_SUBMIT_SUCCESSFUL,
            meta,
          })
          break
        }
        case 'failure': {
          const { status: _, error, ...meta } = result
          fail({
            type: 'IDENTITY',
            event: DiscreteEvent.IDENTITY_SUBMIT_FAILURE,
            reason: formatReason(error),
            meta: { ...formatMeta(error), ...meta },
          })
          break
        }
        default: {
          throw new Error(
            JSON.stringify(
              {
                type: 'IDENTITY',
                reason: `Status not supported: ${
                  (result as Record<string, unknown>).status
                }`,
              },
              null,
              2
            )
          )
        }
      }
    },

    searchEligibility: (result: SearchEligibilityResult) => {
      session.trackEvent(`searchEligibility:${result.status}`)
      switch (result.status) {
        case 'begin': {
          const { status: _, ...meta } = result
          info({
            message: 'Search eligibility by KTN or Biographics begun',
            event: DiscreteEvent.SEARCH_ELIGIBILITY_BY_KTN_OR_BIOGRAPHICS_BEGIN,
            meta,
          })
          break
        }
        case 'success': {
          const { status: _, ...meta } = result
          info({
            message: 'Search eligibility by KTN or Biographics successful',
            event:
              DiscreteEvent.SEARCH_ELIGIBILITY_BY_KTN_OR_BIOGRAPHICS_SUCCESSFUL,
            meta,
          })
          break
        }
        case 'failure': {
          const { status: _, error, ...meta } = result
          fail({
            type: 'ELIGIBILITY',
            event:
              DiscreteEvent.SEARCH_ELIGIBILITY_BY_KTN_OR_BIOGRAPHICS_FAILURE,
            reason: formatReason(error),
            meta: { ...formatMeta(error), ...meta },
          })
          break
        }
        default: {
          throw new Error(
            JSON.stringify(
              {
                type: 'ELIGIBILITY',
                reason: `Status not supported: ${
                  (result as Record<string, unknown>).status
                }`,
              },
              null,
              2
            )
          )
        }
      }
    },

    acceptCoppa: (result: AcceptCoppaResult) => {
      session.trackEvent(`acceptCoppa:${result.status}`)
      switch (result.status) {
        case 'begin': {
          const { status: _, ...meta } = result
          info({
            message: 'Accept Coppa call has begun',
            event: DiscreteEvent.ACCEPT_COPPA_CALL_BEGINS,
            meta,
          })
          break
        }
        case 'success': {
          const { status: _, ...meta } = result
          info({
            message: 'Accept Coppa successful',
            event: DiscreteEvent.ACCEPT_COPPA_CALL_SUCCESSFUL,
            meta,
          })
          break
        }
        case 'failure': {
          const { status: _, error, ...meta } = result
          fail({
            type: 'COPPA',
            event: DiscreteEvent.ACCEPT_COPPA_CALL_FAILURE,
            reason: formatReason(error),
            meta: { ...formatMeta(error), ...meta },
          })
          break
        }
        default: {
          throw new Error(
            JSON.stringify(
              {
                type: 'COPPA',
                reason: `Status not supported: ${
                  (result as Record<string, unknown>).status
                }`,
              },
              null,
              2
            )
          )
        }
      }
    },

    renewalEligibility: (result: RenewalEligibilityResult) => {
      session.trackEvent(`renewalEligibility:${result.status}`)
      switch (result.status) {
        case 'begin': {
          const { status: _, ...meta } = result
          info({
            message:
              'Renewal eligibility search by KTN or Biographics has begun',
            event: DiscreteEvent.RENEWAL_ELIGIBILITY_BEGIN,
            meta,
          })
          break
        }
        case 'success': {
          const { status: _, ...meta } = result
          info({
            message:
              'Renewal eligibility search by KTN or Biographics successful',
            event: DiscreteEvent.RENEWAL_ELIGIBILITY_SUCCESSFUL,
            meta,
          })
          break
        }
        case 'failure': {
          const { status: _, error, ...meta } = result
          fail({
            type: 'RENEWAL_ELIGIBILITY',
            event: DiscreteEvent.RENEWAL_ELIGIBILITY_FAILURE,
            reason: formatReason(error),
            meta: { ...formatMeta(error), ...meta },
          })
          break
        }
        default: {
          throw new Error(
            JSON.stringify(
              {
                type: 'RENEWAL_ELIGIBILITY',
                reason: `Status not supported: ${
                  (result as Record<string, unknown>).status
                }`,
              },
              null,
              2
            )
          )
        }
      }
    },

    fail,

    conclude: () => {
      session.concluded = true
      info({
        message: 'Auth flow concluded',
        event: DiscreteEvent.AUTH_FLOW_CONCLUDED,
        sync: true,
      })
      session.resetSession()
    },
  }
}
